import { useRef } from 'react'
import { BlockComponentFactory } from '../BlockFactory'
import { SegmentedVideoBlockProps } from './block-props.types'
import {
    SegmentedVideoPlayer,
    SegmentedVideoPlayerHandle,
} from '../../../components/VideoPlayer/SegmentedVideoPlayer'
import { LabelBlock } from './LabelBlock'
import Flex from 'antd/lib/flex'
import { LABEL_BOLD_FONT_STYLE } from '../../constants'
import { useBlockMethod } from '../BlockHooks'
import { useSignedURL } from '../../../service/hooks/use-signed-url'
import { toCSSSize } from './block-utils'

const MIN_WIDTH = 600
const MIN_HEIGHT = 300
export const SegmentedVideoBlockComponent =
    BlockComponentFactory.createBlockComponent<SegmentedVideoBlockProps>(props => {
        const {
            src,
            label,
            infoText,
            width,
            height,
            visibility = 'visible',
            style,
            blockId,
        } = props

        const playerRef = useRef<SegmentedVideoPlayerHandle>(null)
        const { url: videoURL, isLoading } = useSignedURL(src)
        useBlockMethod('playSegment', (...args: unknown[]) => {
            const [startTime, endTime] = args as [number, number]
            playerRef.current?.playSegment(startTime, endTime)
        })

        useBlockMethod('setSegment', (...args: unknown[]) => {
            const [startTime, endTime] = args as [number, number]
            playerRef.current?.setSegment(startTime, endTime)
        })

        useBlockMethod('seekToTime', (...args: unknown[]) => {
            const [seconds] = args as [number]
            playerRef.current?.seekToTime(seconds)
        })

        useBlockMethod('getCurrentSegment', () => {
            return playerRef.current?.getCurrentSegment() ?? [0, 0]
        })

        const containerStyle: React.CSSProperties = {
            ...style,
            display: visibility === 'hidden' ? 'none' : 'flex',
            gap: '8px',
            flexDirection: 'column',
            width: toCSSSize(width) || `${MIN_WIDTH}px`, // video js needs width and height to be set
            height: toCSSSize(height) || `${MIN_HEIGHT}px`,
            minWidth: MIN_WIDTH,
            minHeight: MIN_HEIGHT,
        }

        const videoContainerStyle: React.CSSProperties = {
            backgroundColor: 'inherit',
        }

        return (
            <div style={containerStyle}>
                <LabelBlock label={label} infoText={infoText} labelStyle={LABEL_BOLD_FONT_STYLE} />
                {videoURL ? (
                    <Flex style={videoContainerStyle} flex={1} justify="center" align="center">
                        <SegmentedVideoPlayer ref={playerRef} url={videoURL} id={blockId} />
                    </Flex>
                ) : (
                    <Flex style={videoContainerStyle} flex={1} justify="center" align="center">
                        {isLoading ? (
                            'Loading video...'
                        ) : (
                            <SegmentedVideoPlayer url={''} id={blockId} />
                        )}
                    </Flex>
                )}
            </div>
        )
    })
